import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  FileText,
  Search,
  Brain,
  Plus,
  Trash2,
  Eye,
  Download,
  MessageCircle,
  Sparkles,
  AlertCircle
} from 'lucide-react';
import { ragService, PortfolioAnalysis as RAGPortfolioAnalysis } from '../services/ragService';
import { useToast } from '../components/Toast';

interface PortfolioItem {
  id: string;
  title: string;
  description: string;
  fileType: string;
  fileSize: string;
  uploadDate: string;
  tags: string[];
  documentId?: string; // RAG 시스템의 문서 ID
  aiAnalysis?: {
    summary: string;
    keyPoints: string[];
    recommendations: string[];
  };
}

interface ChatMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

const Portfolio: React.FC = () => {
  const toast = useToast();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingId, setAnalyzingId] = useState<string | null>(null);
  const [expandedItem, setExpandedItem] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 파일 목록 API에서 포트폴리오 로드
  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const token = localStorage.getItem('proposalcraft_access_token');
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        const baseUrl = (hostname !== 'localhost' && hostname !== '127.0.0.1')
          ? `${protocol}//${hostname}:8001` : 'http://localhost:8001';

        const response = await fetch(`${baseUrl}/api/files/list?limit=50`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          const files = Array.isArray(data) ? data : (data.files || data.items || []);
          if (files.length > 0) {
            setPortfolioItems(files.map((f: any) => ({
              id: f.id || String(f.file_id),
              title: f.original_name || f.filename || f.title || '파일',
              description: f.description || '',
              fileType: (f.file_type || f.extension || '').toUpperCase(),
              fileSize: f.file_size ? `${(f.file_size / 1024 / 1024).toFixed(1)} MB` : '',
              uploadDate: f.created_at ? new Date(f.created_at).toLocaleDateString('ko-KR') : '',
              tags: f.tags || [],
              documentId: f.document_id || f.id,
            })));
          }
        }
      } catch (err) {
        console.error('Failed to fetch portfolio files:', err);
      }
    };

    fetchPortfolio();
  }, []);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    
    setIsUploading(true);
    
    try {
      // RAG 서비스를 통한 실제 파일 업로드
      const batchResult = await ragService.processBatchDocuments(files);
      
      if (batchResult.success > 0) {
        // 성공적으로 업로드된 파일들을 포트폴리오에 추가
        const newItems: PortfolioItem[] = batchResult.results
          .filter(result => result.success)
          .map((result, index) => ({
            id: result.documentId || Date.now().toString() + index,
            title: result.fileName.replace(/\.[^/.]+$/, ''),
            description: `${result.fileName} 파일이 RAG 시스템에 업로드되었습니다.`,
            fileType: result.fileName.split('.').pop()?.toUpperCase() || 'UNKNOWN',
            fileSize: '처리됨',
            uploadDate: new Date().toISOString().split('T')[0],
            tags: ['RAG 처리됨'],
            documentId: result.documentId
          }));
        
        setPortfolioItems(prev => [...prev, ...newItems]);
      }
      
      // 결과 표시
      console.log(`업로드 완료: ${batchResult.success}개 성공, ${batchResult.failed}개 실패`);
      
    } catch (error) {
      console.error('파일 업로드 오류:', error);
      // 에러 처리
    } finally {
      setIsUploading(false);
    }
  };

  // "새 포트폴리오" 버튼 핸들러 - 숨겨진 파일 입력 트리거
  const handleNewPortfolio = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const fileArray = Array.from(files);
      const batchResult = await ragService.processBatchDocuments(fileArray);

      const newItems: PortfolioItem[] = batchResult.results
        .filter(result => result.success)
        .map((result, index) => ({
          id: result.documentId || `upload-${Date.now()}-${index}`,
          title: result.fileName.replace(/\.[^/.]+$/, ''),
          description: `업로드된 문서 - ${fileArray[index]?.type || '알 수 없음'}`,
          fileType: result.fileName.split('.').pop()?.toUpperCase() || 'FILE',
          fileSize: `${(fileArray[index]?.size / 1024 / 1024).toFixed(1)} MB`,
          uploadDate: new Date().toISOString().split('T')[0],
          tags: [],
          documentId: result.documentId,
        }));

      setPortfolioItems(prev => [...newItems, ...prev]);
      toast.success(`${newItems.length}개 파일이 업로드되었습니다.`);
    } catch (err: any) {
      console.error('Upload failed:', err);
      toast.error('파일 업로드에 실패했습니다.');
    } finally {
      setIsUploading(false);
      if (e.target) e.target.value = '';
    }
  };

  // 보기 버튼 - 카드 확장/축소 토글
  const handleViewItem = (item: PortfolioItem) => {
    setExpandedItem(expandedItem === item.id ? null : item.id);
  };

  // 다운로드 버튼
  const handleDownloadItem = (item: PortfolioItem) => {
    if ((item as any).url) {
      window.open((item as any).url, '_blank');
    } else {
      toast.warning('다운로드 링크가 없습니다.');
    }
  };

  // 삭제 버튼
  const handleDeleteItem = async (item: PortfolioItem) => {
    if (!window.confirm(`"${item.title}"을(를) 삭제하시겠습니까?`)) return;

    try {
      if (item.documentId) {
        await ragService.deleteDocument(item.documentId);
      }
      setPortfolioItems(prev => prev.filter(p => p.id !== item.id));
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error('삭제에 실패했습니다.');
    }
  };

  // 필터 영역 일괄 AI 분석 버튼
  const handleBulkAnalysis = async () => {
    const unanalyzed = portfolioItems.filter(item => !item.aiAnalysis);
    if (unanalyzed.length === 0) {
      toast.info('분석할 항목이 없습니다.');
      return;
    }

    setIsAnalyzing(true);
    for (const item of unanalyzed) {
      if (!item.documentId) continue;
      try {
        const analysis: RAGPortfolioAnalysis = await ragService.analyzePortfolio([item.documentId]);
        setPortfolioItems(prev => prev.map(p =>
          p.id === item.id ? {
            ...p,
            aiAnalysis: {
              summary: analysis.summary,
              keyPoints: analysis.keyPoints,
              recommendations: analysis.recommendations,
            }
          } : p
        ));
      } catch (err) {
        console.error(`Analysis failed for ${item.title}:`, err);
      }
    }
    setIsAnalyzing(false);
  };

  // 카드별 개별 AI 분석 버튼 (analyzingId로 해당 카드만 로딩 표시)
  const handleAnalyzeItem = async (item: PortfolioItem) => {
    if (!item.documentId) {
      toast.warning('분석할 문서 ID가 없습니다.');
      return;
    }
    setAnalyzingId(item.id);
    try {
      const analysis: RAGPortfolioAnalysis = await ragService.analyzePortfolio([item.documentId]);
      setPortfolioItems(prev => prev.map(p =>
        p.id === item.id ? {
          ...p,
          aiAnalysis: {
            summary: analysis.summary,
            keyPoints: analysis.keyPoints,
            recommendations: analysis.recommendations,
          }
        } : p
      ));
    } catch (err: any) {
      console.error('Analysis failed:', err);
      toast.error('AI 분석에 실패했습니다.');
    } finally {
      setAnalyzingId(null);
    }
  };

  const handleAIAnalysis = async (itemId: string) => {
    setIsAnalyzing(true);
    
    try {
      const item = portfolioItems.find(item => item.id === itemId);
      if (!item || !item.documentId) {
        throw new Error('분석할 문서를 찾을 수 없습니다.');
      }
      
      // RAG 서비스를 통한 실제 AI 분석
      const analysis: RAGPortfolioAnalysis = await ragService.analyzePortfolio([item.documentId]);
      
      // 분석 결과를 포트폴리오 아이템에 적용
      setPortfolioItems(prev => prev.map(item => 
        item.id === itemId ? {
          ...item,
          aiAnalysis: {
            summary: analysis.summary,
            keyPoints: analysis.keyPoints,
            recommendations: analysis.recommendations
          }
        } : item
      ));
      
    } catch (error) {
      console.error('AI 분석 오류:', error);
      // 에러 처리 - 사용자에게 알림
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: currentMessage,
      timestamp: new Date()
    };

    setChatMessages(prev => [...prev, userMessage]);
    const question = currentMessage;
    setCurrentMessage('');

    try {
      // RAG 서비스를 통한 실제 질의응답
      const ragResponse = await ragService.askQuestion(question);
      
      const aiMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: ragResponse.answer,
        timestamp: new Date()
      };
      
      setChatMessages(prev => [...prev, aiMessage]);
      
      // 소스 문서 정보도 표시할 수 있음
      if (ragResponse.sources.length > 0) {
        const sourcesMessage: ChatMessage = {
          id: (Date.now() + 2).toString(),
          type: 'ai',
          content: `📚 참고 문서: ${ragResponse.sources.map(s => s.chunk.metadata.title).join(', ')}`,
          timestamp: new Date()
        };
        setChatMessages(prev => [...prev, sourcesMessage]);
      }
      
    } catch (error) {
      console.error('질의응답 오류:', error);
      
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: '죄송합니다. 질문을 처리하는 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        timestamp: new Date()
      };
      
      setChatMessages(prev => [...prev, errorMessage]);
    }
  };

  const filteredItems = portfolioItems.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* 숨겨진 파일 입력 - "새 포트폴리오" 버튼용 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.pptx,.hwp,.txt,.md,.xlsx,.csv"
        onChange={handleFileSelect}
        className="hidden"
      />
      {/* 헤더 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            포트폴리오 관리
          </h1>
          <p className="text-gray-600 mt-2">AI 기반 포트폴리오 분석 및 RAG 시스템</p>
        </div>
        <button onClick={handleNewPortfolio} className="btn-primary flex items-center space-x-2">
          <Plus className="h-5 w-5" />
          <span>새 포트폴리오</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 포트폴리오 목록 */}
        <div className="lg:col-span-2 space-y-6">
          {/* 검색 및 필터 */}
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="포트폴리오 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleBulkAnalysis}
              disabled={isAnalyzing}
              className="btn-secondary flex items-center space-x-2"
            >
              {isAnalyzing ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
              ) : (
                <Brain className="h-5 w-5" />
              )}
              <span>AI 분석</span>
            </button>
          </div>

          {/* 파일 업로드 영역 */}
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-primary-400 transition-colors">
            <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                파일을 드래그 앤 드롭하거나 클릭하여 업로드하세요
              </p>
              <p className="text-xs text-gray-500">
                지원 형식: PDF, DOCX, PPTX, HWP, 이미지 (최대 50MB)
              </p>
            </div>
            <input
              type="file"
              multiple
              accept=".pdf,.docx,.pptx,.hwp,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
              id="portfolio-upload"
            />
            <label htmlFor="portfolio-upload" className="btn-primary mt-4 inline-block cursor-pointer">
              {isUploading ? '업로드 중...' : '파일 선택'}
            </label>
          </div>

          {/* 포트폴리오 목록 */}
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <FileText className="h-6 w-6 text-primary-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                      <span className="px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full">
                        {item.fileType}
                      </span>
                    </div>
                    <p className="text-gray-600 mb-3">{item.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                      <span>📁 {item.fileSize}</span>
                      <span>📅 {item.uploadDate}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {item.tags.map((tag, index) => (
                        <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleAnalyzeItem(item)}
                      disabled={analyzingId === item.id}
                      className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                      title="AI 분석"
                    >
                      {analyzingId === item.id ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                      ) : (
                        <Brain className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleViewItem(item)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="보기"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDownloadItem(item)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="다운로드"
                    >
                      <Download className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="삭제"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* AI 분석 결과 */}
                {item.aiAnalysis && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                      <Sparkles className="h-4 w-4 mr-2" />
                      AI 분석 결과
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <h5 className="text-sm font-medium text-blue-800 mb-1">📋 요약</h5>
                        <p className="text-sm text-blue-700">{item.aiAnalysis.summary}</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h5 className="text-sm font-medium text-blue-800 mb-1">🎯 핵심 포인트</h5>
                          <ul className="text-sm text-blue-700 space-y-1">
                            {item.aiAnalysis.keyPoints.map((point, index) => (
                              <li key={index}>• {point}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <h5 className="text-sm font-medium text-blue-800 mb-1">💡 개선 제안</h5>
                          <ul className="text-sm text-blue-700 space-y-1">
                            {item.aiAnalysis.recommendations.map((rec, index) => (
                              <li key={index}>• {rec}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* RAG 채팅 시스템 */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              AI 포트폴리오 어시스턴트
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              포트폴리오에 대해 질문하거나 AI 분석을 요청하세요.
            </p>

            {/* 채팅 메시지 */}
            <div className="space-y-3 mb-4 max-h-96 overflow-y-auto">
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-3 py-2 rounded-lg ${
                      message.type === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* 메시지 입력 */}
            <form onSubmit={handleChatSubmit} className="flex space-x-2">
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder="포트폴리오에 대해 질문하세요..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                전송
              </button>
            </form>
          </div>

          {/* RAG 시스템 정보 */}
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
            <h3 className="font-medium text-green-900 mb-3 flex items-center">
              <Brain className="h-5 w-5 mr-2" />
              RAG 시스템 정보
            </h3>
            <div className="space-y-2 text-sm text-green-700">
              <p>• 업로드된 문서 기반 지식 검색</p>
              <p>• Claude AI 모델 연동</p>
              <p>• 실시간 문서 분석 및 답변</p>
              <p>• 포트폴리오 최적화 제안</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
